import React, { useCallback, useContext, useMemo } from 'react';

import DataTable from 'components/DataTable';
import DataTableRenderers from 'components/DataTable/DataTable.renderers';
import useLastLoaded from 'components/DataTable/useLastLoaded';
import buildGridOptions from 'components/Grid/buildGridOptions';
import IncompatibleComparison from 'components/IncompatibleComparison';
import { CardSkeleton } from 'components/Skeleton';
import { RowHeightContext } from 'contextProviders/SplashScreenProviders/MiscProviders/RowHeightProvider';
import MetricMatrixContext from 'contexts/MetricMatrixContext';
import { useRowHighlightPredicate } from 'hooks/useRowHighlighter';

import useRankingMatrixContent from './useRankingMatrixContent';

// The Ranking matrix drawn by DataTable (dataTable gate). Same colDefs,
// rows, totals and renderers as the AG view, with the rows as a prop. A
// refetch keeps the last loaded table up under the loading overlay rather
// than going back to a skeleton.
const RankingMatrixDataTableView = ({
  gadget,
  isCompatibleComparison,
  isLoading,
  colDefs,
  terms,
  totals,
  visId,
  onDrillDown,
  setExportHandle,
}: ReturnType<typeof useRankingMatrixContent> & {
  gadget: VisualisationDefinitions.RankingMatrix;
  setExportHandle?: (handle: DataTable.ExportHandle | undefined) => void;
}) => {
  const { rowHeight } = useContext(RowHeightContext);
  const isRowHighlighted = useRowHighlightPredicate({
    drillField: gadget.groupByField,
  });
  const isRowSelected = useCallback(
    (data: DataTable.Row) => isRowHighlighted(data) === true,
    [isRowHighlighted],
  );
  const defaultColDef = useMemo(
    () =>
      buildGridOptions({ rowModelType: 'clientSide', rowHeight }).defaultColDef,
    [rowHeight],
  );
  const context = useMemo(() => ({ onDrillDown }), [onDrillDown]);
  const loaded = useLastLoaded({
    value: useMemo(
      () => ({ colDefs, terms, totals }),
      [colDefs, terms, totals],
    ),
    isLoading: isLoading || !terms,
  });

  // One value per onDrillDown: a new object each render would re-render
  // every cell that reads it (as the matrix's card resizes)
  const metricMatrixContextValue = useMemo(
    () => ({ onDrillDown }),
    [onDrillDown],
  );

  if (!isCompatibleComparison) {
    return <IncompatibleComparison />;
  }

  if (!loaded || !loaded.terms) {
    return <CardSkeleton />;
  }

  const isFitContent = gadget.gridSizing === 'fitContent';
  return (
    <MetricMatrixContext.Provider value={metricMatrixContextValue}>
      <DataTable
        tableId={visId}
        testId="grid"
        columnDefs={loaded.colDefs}
        defaultColDef={defaultColDef}
        components={DataTableRenderers}
        rows={loaded.terms}
        totalsRows={loaded.totals ?? undefined}
        context={context}
        sizing={isFitContent ? 'fitContent' : 'fitWidth'}
        // As the AG view's autoSizeAllColumns(skipHeader), which only runs
        // when fitting the content
        skipHeaderWhenSizing
        isRowSelected={isRowSelected}
        isLoading={isLoading}
        isHeadlessHeightEnabled
        onExportHandle={setExportHandle}
      />
    </MetricMatrixContext.Provider>
  );
};

export default RankingMatrixDataTableView;
